import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Modal, Form, Toast, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getShopByOwner, getShopProducts, getShopProductsPaginated, getProductsBatchWithCursors, getProductsCount, addProduct, updateProduct, deleteProduct, toggleProductDisabled, getShopCategories, updateShopCategories, syncProductCategory, getAllProductCategories } from '../../services/shopService';
import { uploadImage, deleteImage } from '../../services/cloudinaryService';
import Pagination from '../../components/Pagination';
import SearchBar from '../../components/SearchBar';
import Papa from 'papaparse';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const MAX_IMAGE_DIMENSIONS = 2048; // 2048x2048 pixels

const Products = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    stock: '',
    category: '',
    image: null,
    imagePreview: null,
    imagePublicId: null,
    imageUrl: '',
    infiniteStock: false,
    disabled: false
  });
  const [error, setError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [toastVariant, setToastVariant] = useState('success');
  const [importing, setImporting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showBulkDisableModal, setShowBulkDisableModal] = useState(false);
  const [bulkDisableAction, setBulkDisableAction] = useState(false); // true = disabilita, false = abilita

  // Stati per la paginazione
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [cursors, setCursors] = useState([]); // cursors[i] = lastVisible della pagina i+1
  const [productsPerPage, setProductsPerPage] = useState(25);

  useEffect(() => {
    loadShopAndProducts();
  }, [currentUser, currentPage, searchQuery, filterCategory, productsPerPage]);

  const loadShopAndProducts = async (pageDoc = null) => {
    try {
      setLoading(true);
      const shops = await getShopByOwner(currentUser.uid);
      if (shops.length > 0) {
        setShop(shops[0]);

        if (searchQuery.trim()) {
          // Ricerca: carica TUTTI i prodotti e filtra client-side per substring matching + categoria
          const [allProducts, shopCategories, productCategories] = await Promise.all([
            getShopProducts(shops[0].id),
            getShopCategories(shops[0].id),
            getAllProductCategories(shops[0].id)
          ]);
          const q = searchQuery.toLowerCase();
          let filtered = allProducts.filter(p =>
            p.name.toLowerCase().includes(q)
          );
          if (filterCategory) {
            filtered = filtered.filter(p => p.category === filterCategory);
          }
          setTotalPages(1);
          setProducts(filtered);
          setCursors([]);
          setCategories([...new Set([...shopCategories, ...productCategories])].sort());
        } else {
          // Normale: paginazione server-side (con filtro categoria se impostato)
          const filters = {};
          if (filterCategory) {
            filters.category = filterCategory;
          }

          // Costruisci la catena di cursori se la pagina richiesta è oltre quelle memorizzate
          let startAfterDoc = null;
          let updatedCursors = [...cursors];

          if (currentPage > 1) {
            if (currentPage - 2 < updatedCursors.length && updatedCursors[currentPage - 2]) {
              // Cursore già disponibile — lo usa direttamente
              startAfterDoc = updatedCursors[currentPage - 2];
            } else {
              // Cursore mancante: carica a stride (blocchi di STRIDE_PAGES) dall'ultimo cursore noto.
              // Molto più veloce del sequenziale: per pagina 30 con stride 5,
              // ~6 query invece di ~29.
              const STRIDE_PAGES = 5;
              let cursor = null;
              let lastKnownPage = 0;
              for (let i = updatedCursors.length - 1; i >= 0; i--) {
                if (updatedCursors[i]) {
                  cursor = updatedCursors[i];
                  lastKnownPage = i + 1;
                  break;
                }
              }

              // Fase 1: salta in blocchi da STRIDE_PAGES pagine, estraendo cursori intermedi
              while (lastKnownPage + STRIDE_PAGES <= currentPage - 1) {
                const batchSize = STRIDE_PAGES * productsPerPage;
                const { pageCursors } = await getProductsBatchWithCursors(
                  shops[0].id, batchSize, productsPerPage, cursor, filters
                );
                if (pageCursors.length === 0) break;
                for (let j = 0; j < pageCursors.length; j++) {
                  updatedCursors[lastKnownPage + j] = pageCursors[j];
                }
                cursor = pageCursors[pageCursors.length - 1];
                lastKnownPage += pageCursors.length;
              }

              // Fase 2: completa le pagine rimanenti (al massimo STRIDE_PAGES - 1)
              for (let p = lastKnownPage + 1; p < currentPage; p++) {
                const { lastVisible: lv } = await getShopProductsPaginated(
                  shops[0].id, productsPerPage, cursor, filters
                );
                if (lv) {
                  cursor = lv;
                  updatedCursors[p - 1] = lv;
                } else {
                  break;
                }
              }
              startAfterDoc = cursor;
            }
          }

          const [totalCount, { products: productsList, lastVisible }, shopCategories, productCategories] = await Promise.all([
            getProductsCount(shops[0].id, filters),
            getShopProductsPaginated(shops[0].id, productsPerPage, startAfterDoc, filters),
            getShopCategories(shops[0].id),
            getAllProductCategories(shops[0].id)
          ]);
          setTotalPages(Math.ceil(totalCount / productsPerPage));
          setProducts(productsList);
          updatedCursors[currentPage - 1] = lastVisible;
          setCursors(updatedCursors);
          setCategories([...new Set([...shopCategories, ...productCategories])].sort());
        }
      }
    } catch (error) {
      console.error('Errore nel caricamento dei dati:', error);
      setError(t('errors.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const validateImage = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(t('errors.imageTooLarge'));
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        if (img.width > MAX_IMAGE_DIMENSIONS || img.height > MAX_IMAGE_DIMENSIONS) {
          reject(new Error(t('errors.imageDimensionsTooLarge')));
        }
        resolve();
      };
      img.onerror = () => reject(new Error(t('errors.invalidImage')));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      await validateImage(file);
      setUploading(true);
      
      // Carica l'immagine su Cloudinary
      const result = await uploadImage(file);
      
      setFormData(prev => ({
        ...prev,
        image: result.url,
        imagePreview: result.url,
        imagePublicId: result.publicId
      }));
    } catch (error) {
      setError(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleImageUrlSubmit = async (e) => {
    e.preventDefault();
    if (!formData.imageUrl) return;

    try {
      setUploading(true);
      setError(''); // Reset degli errori precedenti
      
      // Validazione base dell'URL
      try {
        new URL(formData.imageUrl);
      } catch (e) {
        throw new Error('Link non valido');
      }

      // Carica l'immagine da URL su Cloudinary
      const result = await uploadImage(formData.imageUrl, true);
      
      setFormData(prev => ({
        ...prev,
        image: result.url,
        imagePreview: result.url,
        imagePublicId: result.publicId,
        imageUrl: '' // Reset dell'URL dopo il caricamento
      }));
    } catch (error) {
      console.error('Errore nel caricamento dell\'immagine:', error);
      setError(error.message === 'Link non valido' ? error.message : t('errors.imageUploadError'));
    } finally {
      setUploading(false);
    }
  };

  const handleImageRemove = () => {
    setFormData(prev => ({
      ...prev,
      image: null,
      imagePreview: null,
      imagePublicId: null
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!shop) return;

    // Validazione: nuova prodotto richiede immagine
    if (!editingProduct && !formData.image) {
      setError(t('errors.imageRequired') || 'È necessario caricare almeno un\'immagine per il prodotto');
      return;
    }

    try {
      const productData = {
        name: formData.name || '',
        description: formData.description || '',
        price: parseFloat(formData.price) || 0,
        stock: formData.infiniteStock ? null : (parseInt(formData.stock) || 0),
        infiniteStock: !!formData.infiniteStock,
        category: formData.category || '',
        image: formData.image || null,
        imagePublicId: formData.imagePublicId || null,
        disabled: !!formData.disabled
      };

      if (editingProduct) {
        // Elimina la vecchia immagine solo se è stata cambiata o rimossa
        if (editingProduct.imagePublicId && editingProduct.imagePublicId !== formData.imagePublicId) {
          await deleteImage(editingProduct.imagePublicId);
        }
        await updateProduct(shop.id, editingProduct.id, productData);
        await syncProductCategory(shop.id, formData.category);
        setToastMessage(t('success.productUpdated'));
      } else {
        await addProduct(shop.id, productData);
        await syncProductCategory(shop.id, formData.category);
        setToastMessage(t('success.productAdded'));
      }

      setShowModal(false);
      setShowToast(true);
      if (!editingProduct) {
        // Torna a pagina 1 solo per nuovi prodotti
        setCurrentPage(1);
        setCursors([]);
      }
      loadShopAndProducts(null);
      resetForm();
    } catch (error) {
      console.error('Errore nel salvataggio del prodotto:', error);
      setError(t('errors.saveError'));
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      stock: product.infiniteStock ? '' : product.stock.toString(),
      category: product.category || '',
      image: product.image,
      imagePreview: product.image,
      imagePublicId: product.imagePublicId,
      imageUrl: '',
      infiniteStock: !!product.infiniteStock,
      disabled: !!product.disabled
    });
    setShowModal(true);
  };

  const handleAddCategory = async () => {
    if (newCategory && !categories.includes(newCategory)) {
      try {
        const updatedCategories = [...categories, newCategory];
        await updateShopCategories(shop.id, updatedCategories);
        setCategories(updatedCategories);
        setNewCategory('');
        setToastMessage('Categoria aggiunta con successo');
        setToastVariant('success');
        setShowToast(true);
      } catch (error) {
        console.error('Errore nell\'aggiunta della categoria:', error);
        setToastMessage('Errore nell\'aggiunta della categoria');
        setToastVariant('danger');
        setShowToast(true);
      }
    }
  };

  const handleRemoveCategory = async (categoryToRemove) => {
    try {
      const updatedCategories = categories.filter(cat => cat !== categoryToRemove);
      await updateShopCategories(shop.id, updatedCategories);
      setCategories(updatedCategories);
      setToastMessage('Categoria rimossa con successo');
      setToastVariant('success');
      setShowToast(true);
    } catch (error) {
      console.error('Errore nella rimozione della categoria:', error);
      setToastMessage('Errore nella rimozione della categoria');
      setToastVariant('danger');
      setShowToast(true);
    }
  };

  const handleDelete = (product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;
    try {
      // Elimina l'immagine da Cloudinary se esiste
      if (productToDelete.imagePublicId) {
        await deleteImage(productToDelete.imagePublicId);
      }

      await deleteProduct(shop.id, productToDelete.id);
      setShowDeleteModal(false);
      setProductToDelete(null);
      setToastMessage(t('success.productDeleted'));
      setShowToast(true);
      loadShopAndProducts(null);
    } catch (error) {
      console.error('Errore nell\'eliminazione del prodotto:', error);
      setError(t('errors.deleteError'));
    }
  };

  // Seleziona/deseleziona un singolo prodotto
  const toggleSelectOne = (productId) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  // Seleziona/deseleziona tutti i prodotti visibili
  const toggleSelectAll = () => {
    if (selectedProducts.size === products.length && products.length > 0) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  };

  // Apre il modal di conferma eliminazione multipla
  const handleBulkDelete = () => {
    setShowBulkDeleteModal(true);
  };

  // Apre il modal per disabilitare/abilitare prodotti multipli
  const handleBulkDisable = (disable = true) => {
    setBulkDisableAction(disable);
    setShowBulkDisableModal(true);
  };

  // Esegue l'azione di disabilitazione/abilitazione multipla
  const confirmBulkDisable = async () => {
    if (selectedProducts.size === 0) return;
    try {
      const productsToToggle = products.filter(p => selectedProducts.has(p.id));
      await Promise.all(
        productsToToggle.map(async (product) => {
          await toggleProductDisabled(shop.id, product.id, bulkDisableAction);
        })
      );
      setSelectedProducts(new Set());
      setShowBulkDisableModal(false);
      setToastMessage(`${productsToToggle.length} prodotti ${bulkDisableAction ? 'disabilitati' : 'abilitati'} con successo`);
      setToastVariant('success');
      setShowToast(true);
      loadShopAndProducts(null);
    } catch (error) {
      console.error('Errore nella modifica multipla:', error);
      setToastMessage('Errore nella modifica dei prodotti');
      setToastVariant('danger');
      setShowToast(true);
    }
  };

  // Disabilita/abilita un singolo prodotto
  const handleToggleDisabled = async (product) => {
    try {
      await toggleProductDisabled(shop.id, product.id, !product.disabled);
      setToastMessage(product.disabled ? 'Prodotto abilitato con successo' : 'Prodotto disabilitato con successo');
      setToastVariant('success');
      setShowToast(true);
      loadShopAndProducts(null);
    } catch (error) {
      console.error('Errore nella modifica del prodotto:', error);
      setToastMessage('Errore nella modifica del prodotto');
      setToastVariant('danger');
      setShowToast(true);
    }
  };

  // Conferma l'eliminazione multipla
  const confirmBulkDelete = async () => {
    if (selectedProducts.size === 0) return;
    try {
      const productsToDelete = products.filter(p => selectedProducts.has(p.id));
      // Elimina in parallelo
      await Promise.all(
        productsToDelete.map(async (product) => {
          if (product.imagePublicId) {
            await deleteImage(product.imagePublicId);
          }
          await deleteProduct(shop.id, product.id);
        })
      );
      setSelectedProducts(new Set());
      setShowBulkDeleteModal(false);
      setToastMessage(`${productsToDelete.length} prodotti eliminati con successo`);
      setToastVariant('success');
      setShowToast(true);
      loadShopAndProducts(null);
    } catch (error) {
      console.error('Errore nell\'eliminazione multipla:', error);
      setToastMessage('Errore nell\'eliminazione dei prodotti');
      setToastVariant('danger');
      setShowToast(true);
    }
  };

  const [searchInput, setSearchInput] = useState('');

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
    setCursors([]);
    setSelectedProducts(new Set());
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      stock: '',
      category: '',
      image: null,
      imagePreview: null,
      imagePublicId: null,
      imageUrl: '',
      infiniteStock: false,
      disabled: false
    });
    setEditingProduct(null);
    setError('');
  };

  // Funzione per esportare prodotti in CSV
  const handleExportCSV = () => {
    if (!products.length) return;
    const csv = Papa.unparse(products.map(({ id, ...rest }) => rest));
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'prodotti.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Funzione per importare prodotti da CSV
  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const imported = results.data;
          for (const prod of imported) {
            // Conversione dei campi numerici
            prod.price = parseFloat(prod.price);
            prod.stock = prod.infiniteStock === 'true' ? null : parseInt(prod.stock);
            prod.infiniteStock = prod.infiniteStock === 'true';

            // Gestione immagine: se non è Cloudinary, carica e sostituisci
            if (prod.image && !prod.image.startsWith('https://res.cloudinary.com')) {
              try {
                const uploadRes = await uploadImage(prod.image, true); // true indica upload da URL
                prod.image = uploadRes.url;
                prod.imagePublicId = uploadRes.publicId;
              } catch (err) {
                // Se fallisce il caricamento, salta il prodotto o gestisci errore
                continue;
              }
            }
            await addProduct(shop.id, prod);
            // Sincronizza la categoria nel documento shop
            if (prod.category) {
              await syncProductCategory(shop.id, prod.category);
            }
          }
          setToastMessage('Prodotti importati con successo');
          setToastVariant('success');
          setShowToast(true);
          setCurrentPage(1);
          setCursors([]);
          loadShopAndProducts(null);
        } catch (err) {
          setToastMessage('Errore durante l\'importazione');
          setToastVariant('danger');
          setShowToast(true);
        } finally {
          setImporting(false);
          e.target.value = '';
        }
      },
      error: () => {
        setToastMessage('Errore lettura file CSV');
        setToastVariant('danger');
        setShowToast(true);
        setImporting(false);
        e.target.value = '';
      }
    });
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Container>
    );
  }

  if (!shop) {
    return (
      <Container className="py-5">
        <Card>
          <Card.Body className="text-center">
            <h3>{t('errors.noShopFound')}</h3>
            <Button variant="primary" onClick={() => navigate('/seller/settings')}>
              {t('actions.createShop')}
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="mb-4">
        <Col>
          <h2>{t('products.title')}</h2>
        </Col>
        <Col xs="auto" className="d-flex align-items-center gap-2">
          <Button variant="primary" as="label" className="d-flex align-items-center" disabled={importing}>
            <i className="bi bi-upload me-1"></i> Importa CSV
            <input type="file" accept=".csv" onChange={handleImportCSV} hidden disabled={importing} />
          </Button>
          <Button variant="primary" onClick={handleExportCSV} className="d-flex align-items-center">
            <i className="bi bi-download me-1"></i> Esporta CSV
          </Button>
          <Button variant="primary" onClick={() => {
            resetForm();
            setShowModal(true);
          }} className="d-flex align-items-center" disabled={importing}>
            <i className="bi bi-plus-lg me-1"></i> {t('products.addNew')}
          </Button>
        </Col>
      </Row>

      <SearchBar
        placeholder={t('shop.search_products')}
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        onSearch={handleSearch}
      />

      <div className="mb-3 d-flex align-items-center gap-3 flex-wrap">
        <Form.Select
          value={filterCategory}
          onChange={(e) => {
            setFilterCategory(e.target.value);
            setCurrentPage(1);
            setCursors([]);
            setSelectedProducts(new Set());
          }}
          style={{ maxWidth: '300px' }}
        >
          <option value="">{t('shop.all_categories')}</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </Form.Select>

        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small text-nowrap">Prodotti per pagina:</span>
          <Form.Select
            value={productsPerPage}
            onChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              setProductsPerPage(newSize);
              setCurrentPage(1);
              setCursors([]);
            }}
            style={{ width: '80px' }}
            size="sm"
          >
            <option value="25">25</option>
            <option value="50">50</option>
          </Form.Select>
        </div>
      </div>

      <Card>
        <Card.Body>
          {selectedProducts.size > 0 && (
            <div className="d-flex align-items-center justify-content-between mb-3 p-2 rounded-3" style={{ backgroundColor: '#f8d7da' }}>
              <span className="text-danger fw-medium">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {selectedProducts.size} prodotto{selectedProducts.size !== 1 ? 'i' : ''} selezionato{selectedProducts.size !== 1 ? 'i' : ''}
              </span>
              <div className="d-flex gap-2">
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => handleBulkDisable(true)}
                >
                  <i className="bi bi-eye-slash me-1"></i> Disabilita selezionati
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => handleBulkDisable(false)}
                >
                  <i className="bi bi-eye me-1"></i> Abilita selezionati
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleBulkDelete}
                >
                  <i className="bi bi-trash me-1"></i> Elimina selezionati
                </Button>
              </div>
            </div>
          )}
          <Table responsive hover>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <Form.Check
                    type="checkbox"
                    checked={products.length > 0 && selectedProducts.size === products.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>#</th>
                <th>{t('products.image')}</th>
                <th>{t('products.name')}</th>
                <th>{t('products.category')}</th>
                <th>{t('products.price')}</th>
                <th>{t('products.stock')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, index) => (
                <tr key={product.id} className={`${selectedProducts.has(product.id) ? 'table-active' : ''} ${product.disabled ? 'opacity-50' : ''}`}>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={selectedProducts.has(product.id)}
                      onChange={() => toggleSelectOne(product.id)}
                    />
                  </td>
                  <td className="text-muted small">{(currentPage - 1) * productsPerPage + index + 1}</td>
                  <td>
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.name}
                        style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div
                        className="bg-light d-flex align-items-center justify-content-center"
                        style={{ width: '50px', height: '50px' }}
                      >
                        <i className="bi bi-image text-secondary"></i>
                      </div>
                    )}
                  </td>
                  <td>{product.name}</td>
                  <td>
                    <span className="badge bg-light text-dark">
                      {product.category || '-'}
                    </span>
                  </td>
                  <td>€{product.price.toFixed(2)}</td>
                  <td>{product.infiniteStock ? <span title="Stock infinito">&#8734;</span> : product.stock}</td>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-flex align-items-center gap-1">
                        <i className="bi bi-three-dots"></i>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleEdit(product)}>
                          <i className="bi bi-pencil me-2"></i> {t('common.edit')}
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleToggleDisabled(product)}>
                          <i className={`bi ${product.disabled ? 'bi-eye' : 'bi-eye-slash'} me-2`}></i>
                          {product.disabled ? 'Abilita' : 'Disabilita'}
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={() => handleDelete(product)} className="text-danger">
                          <i className="bi bi-trash me-2"></i> {t('common.delete')}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => {
        setShowModal(false);
        resetForm();
      }}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingProduct ? t('products.editProduct') : t('products.addProduct')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>{t('products.image')}</Form.Label>
              <div className="mb-3">
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  disabled={uploading}
                />
                {uploading && (
                  <div className="mt-2">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Caricamento...</span>
                    </div>
                    <span className="ms-2">Caricamento immagine...</span>
                  </div>
                )}
              </div>
              
              <div className="mb-3">
                <Form.Label>{t('products.imageUrl')}</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type="url"
                    placeholder={t('products.imageUrlPlaceholder')}
                    value={formData.imageUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, imageUrl: e.target.value }))}
                    disabled={uploading}
                    className={error ? 'is-invalid' : ''}
                  />
                  <Button 
                    variant="light"
                    onClick={handleImageUrlSubmit}
                    disabled={!formData.imageUrl || uploading}
                    className="d-flex align-items-center border border-start-0"
                    style={{
                      borderColor: '#ced4da',
                      borderRadius: '0 16px 16px 0',
                      transition: 'all 0.2s ease-in-out'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#e9ecef';
                      e.currentTarget.style.borderColor = '#ced4da';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#fff';
                      e.currentTarget.style.borderColor = '#ced4da';
                    }}
                  >
                    {uploading ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : (
                      <i className="bi bi-cloud-upload"></i>
                    )}
                  </Button>
                </div>
                {error && (
                  <div className="invalid-feedback d-block">
                    {error}
                  </div>
                )}
                <Form.Text className="text-muted">
                  {t('products.imageUrlHelp')}
                </Form.Text>
              </div>

              {formData.imagePreview && !uploading && (
                <div className="mt-2 position-relative d-inline-block">
                  <img
                    src={formData.imagePreview}
                    alt="Preview"
                    style={{ maxWidth: '200px', maxHeight: '200px' }}
                    className="img-thumbnail"
                  />
                  <button
                    type="button"
                    className="btn-close position-absolute top-0 end-0 m-1 bg-white rounded-circle p-1"
                    style={{ fontSize: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                    onClick={handleImageRemove}
                    aria-label="Rimuovi immagine"
                    title="Rimuovi immagine"
                  ></button>
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('products.name')}</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('products.category')}</Form.Label>
              <div className="d-flex gap-2 mb-2">
                <Form.Select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  required
                >
                  <option value="">{t('products.selectCategory')}</option>
                  {/* Include la categoria del prodotto in modifica anche se non è nella lista shop */}
                  {(formData.category && !categories.includes(formData.category)
                    ? [formData.category, ...categories]
                    : categories
                  ).map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </Form.Select>
                <Button 
                  variant="outline-primary" 
                  onClick={() => setShowCategoryModal(true)}
                  type="button"
                  className="px-2"
                  title={t('products.manageCategories')}
                >
                  <i className="bi bi-tags"></i>
                </Button>
              </div>
              <Form.Text className="text-muted">
                {t('products.categoryHelp')}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('products.description')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('products.price')}</Form.Label>
              <Form.Control
                type="text"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('products.stock')}</Form.Label>
              <Form.Control
                type="text"
                value={formData.stock}
                onChange={(e) => setFormData(prev => ({ ...prev, stock: e.target.value }))}
                required={!formData.infiniteStock}
                disabled={formData.infiniteStock}
              />
              <Form.Check
                type="switch"
                id="infinite-stock-switch"
                label={t('products.infiniteStock') || 'Stock infinito'}
                checked={!!formData.infiniteStock}
                onChange={e => setFormData(prev => ({ ...prev, infiniteStock: e.target.checked }))}
                className="mt-2"
              />
              <Form.Check
                type="switch"
                id="disabled-switch"
                label="Prodotto disabilitato"
                checked={!!formData.disabled}
                onChange={e => setFormData(prev => ({ ...prev, disabled: e.target.checked }))}
                className="mt-2"
              />
              {formData.disabled && (
                <Form.Text className="text-warning">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  I prodotti disabilitati non vengono mostrati nello shop.
                </Form.Text>
              )}
            </Form.Group>

            {error && (
              <div className="alert alert-danger">
                {error}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => {
                setShowModal(false);
                resetForm();
              }}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={uploading}>
                {editingProduct ? t('common.save') : t('common.add')}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showCategoryModal} onHide={() => setShowCategoryModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('products.manageCategories')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t('products.addNewCategory')}</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder={t('products.categoryName')}
              />
              <Button variant="primary" onClick={handleAddCategory}>
                {t('common.add')}
              </Button>
            </div>
          </Form.Group>

          <div className="mt-4">
            <h6>{t('products.existingCategories')}</h6>
            <div className="d-flex flex-wrap gap-2">
              {categories.map((category) => (
                <div 
                  key={category} 
                  className="badge bg-light text-dark d-flex align-items-center"
                  style={{ padding: '8px 12px', fontSize: '0.9rem' }}
                >
                  {category}
                  <button
                    type="button"
                    className="btn-close ms-2"
                    style={{ fontSize: '0.5rem' }}
                    onClick={() => handleRemoveCategory(category)}
                    aria-label="Remove"
                  ></button>
                </div>
              ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCategoryModal(false)}>
            {t('common.close')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal di conferma eliminazione */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('confirmations.deleteProductTitle') || 'Conferma eliminazione'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            {t('confirmations.deleteProduct')}{' '}
            <strong>{productToDelete?.name}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            <i className="bi bi-trash me-1"></i> {t('common.delete')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal di conferma eliminazione multipla */}
      <Modal show={showBulkDeleteModal} onHide={() => setShowBulkDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Conferma eliminazione multipla</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Sei sicuro di voler eliminare <strong>{selectedProducts.size}</strong> prodotto{selectedProducts.size !== 1 ? 'i' : ''}?
          </p>
          <div className="mt-3">
            <p className="text-muted small mb-1">Prodotti da eliminare:</p>
            <ul className="mb-0 small">
              {products.filter(p => selectedProducts.has(p.id)).map(p => (
                <li key={p.id}>{p.name}</li>
              ))}
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkDeleteModal(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={confirmBulkDelete}>
            <i className="bi bi-trash me-1"></i> Elimina {selectedProducts.size} prodotti
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal di conferma disabilitazione/abilitazione multipla */}
      <Modal show={showBulkDisableModal} onHide={() => setShowBulkDisableModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{bulkDisableAction ? 'Conferma disabilitazione multipla' : 'Conferma abilitazione multipla'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Sei sicuro di voler {bulkDisableAction ? 'disabilitare' : 'abilitare'} <strong>{selectedProducts.size}</strong> prodotto{selectedProducts.size !== 1 ? 'i' : ''}?
          </p>
          <div className="mt-3">
            <p className="text-muted small mb-1">Prodotti interessati:</p>
            <ul className="mb-0 small">
              {products.filter(p => selectedProducts.has(p.id)).map(p => (
                <li key={p.id}>{p.name}</li>
              ))}
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkDisableModal(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant={bulkDisableAction ? 'warning' : 'success'} onClick={confirmBulkDisable}>
            <i className={`bi ${bulkDisableAction ? 'bi-eye-slash' : 'bi-eye'} me-1`}></i>
            {bulkDisableAction ? 'Disabilita' : 'Abilita'} {selectedProducts.size} prodotti
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Overlay di caricamento per import CSV */}
      {importing && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}
        >
          <div className="spinner-border text-light mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Importazione in corso...</span>
          </div>
          <span className="text-light fw-semibold">Importazione prodotti in corso...</span>
        </div>
      )}

      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        delay={3000}
        autohide
        style={{ position: 'fixed', bottom: 20, right: 20 }}
      >
        <Toast.Header>
          <strong className="me-auto">{t('common.notification')}</strong>
        </Toast.Header>
        <Toast.Body>{toastMessage}</Toast.Body>
      </Toast>
    </Container>
  );
};

export default Products; 